import AccountUsageIndicator from '../../components/AccountUsageIndicator'
import Modal from '../../components/Modal'
import TimezoneSelect from '../../components/TimezoneSelect'
import { useI18n } from '../../i18n'

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

function configDisplayName(configs, configId, t) {
  if (configId == null || configId === '') return t('agencySettings.models.defaultConfigActive')
  const match = configs.find((config) => String(config.id) === String(configId))
  return match?.name || `Config #${configId}`
}

/**
 * Секция аккаунта на странице детали модели.
 *
 * @param {{ model, account, loadingAccount, accountUsageOk, accountUsageTitle, showAccountEdit, setShowAccountEdit, accountDescriptionDraft, setAccountDescriptionDraft, savingAccountDescription, onSaveAccountDescription }} props
 */
export default function ModelDetailAccountSection({
  model,
  account,
  loadingAccount,
  accountUsageOk,
  accountUsageTitle,
  showAccountEdit,
  setShowAccountEdit,
  accountDescriptionDraft,
  setAccountDescriptionDraft,
  timezoneDraft,
  setTimezoneDraft,
  maxMessagePriceDraft,
  setMaxMessagePriceDraft,
  defaultConfigIdDraft,
  setDefaultConfigIdDraft,
  configs = [],
  canManageModelDefaultConfig = false,
  savingAccountDescription,
  onSaveAccountDescription,
}) {
  const { t } = useI18n()

  return (
    <section
      className="model-detail-account-block"
      aria-label={t('modelDetail.account.sectionLabel')}
    >
      <div className="model-detail-section-row">
        <div className="model-detail-account-title-wrap">
          <h2 className="model-detail-media-section-title">
            <span className="model-detail-account-login">
              {model.of_email || t('common.notAvailable')}
            </span>
            <AccountUsageIndicator
              isReady={accountUsageOk}
              title={accountUsageTitle}
              className="model-detail-account-usage-indicator"
            />
          </h2>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setAccountDescriptionDraft(model?.account_description || '')
            setTimezoneDraft(model?.timezone || '')
            setMaxMessagePriceDraft?.(
              model?.max_message_price != null ? String(model.max_message_price) : ''
            )
            setDefaultConfigIdDraft?.(
              model?.default_config_id != null ? String(model.default_config_id) : ''
            )
            setShowAccountEdit(true)
          }}
          aria-label={t('modelDetail.account.editAria')}
        >
          <PencilIcon />
        </button>
      </div>

      {loadingAccount && (
        <p className="model-detail-account-loading">{t('modelDetail.account.loading')}</p>
      )}
      {!loadingAccount && (account || model.account_description || model.timezone || model.default_config_id != null) && (
        <dl className="model-detail-account-dl">
          {account && (
            <>
              {account.name != null && account.name !== '' && (
                <>
                  <dt>{t('modelDetail.account.name')}</dt>
                  <dd>{account.name}</dd>
                </>
              )}
              {account.username != null && account.username !== '' && (
                <>
                  <dt>Username</dt>
                  <dd>{account.username}</dd>
                </>
              )}
              {account.email != null && account.email !== '' && (
                <>
                  <dt>Email</dt>
                  <dd>{account.email}</dd>
                </>
              )}
              {account.subscribePrice != null && (
                <>
                  <dt>{t('modelDetail.account.subscribePrice')}</dt>
                  <dd>{account.subscribePrice}</dd>
                </>
              )}
              {(account.creditsMin != null || account.creditsMax != null) && (
                <>
                  <dt>{t('modelDetail.account.credits')}</dt>
                  <dd>
                    {account.creditsMin ?? '—'} – {account.creditsMax ?? '—'}
                  </dd>
                </>
              )}
              {account.subscribersCount != null && (
                <>
                  <dt>{t('modelDetail.account.subscribers')}</dt>
                  <dd>{account.subscribersCount}</dd>
                </>
              )}
              {account.postsCount != null && (
                <>
                  <dt>{t('modelDetail.account.posts')}</dt>
                  <dd>{account.postsCount}</dd>
                </>
              )}
            </>
          )}

          {(model.account_description || model.timezone || model.default_config_id != null) && (
            <>
              {account && <div className="model-detail-account-dl-sep" />}
              {model.account_description && (
                <>
                  <dt>{t('modelDetail.account.localDescription')}</dt>
                  <dd>{model.account_description}</dd>
                </>
              )}
              {model.timezone && (
                <>
                  <dt>{t('modelDetail.account.timezone')}</dt>
                  <dd>{model.timezone}</dd>
                </>
              )}
              {model.max_message_price != null && (
                <>
                  <dt>{t('modelDetail.account.maxMessagePrice.label')}</dt>
                  <dd>${model.max_message_price}</dd>
                </>
              )}
              {model.default_config_id != null && (
                <>
                  <dt>{t('modelDetail.account.defaultConfig.label')}</dt>
                  <dd>{configDisplayName(configs, model.default_config_id, t)}</dd>
                </>
              )}
            </>
          )}
        </dl>
      )}

      <Modal
        open={showAccountEdit}
        title={t('modelDetail.account.description.modalTitle')}
        titleId="account-edit-modal-title"
        modalClassName="modal--account-edit"
        onClose={() => !savingAccountDescription && setShowAccountEdit(false)}
        footer={
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => !savingAccountDescription && setShowAccountEdit(false)}
              disabled={savingAccountDescription}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              form="account-edit-form"
              className="btn btn-primary btn-sm"
              disabled={savingAccountDescription}
            >
              {savingAccountDescription ? t('common.saving') : t('common.save')}
            </button>
          </div>
        }
      >
        <form id="account-edit-form" onSubmit={onSaveAccountDescription} className="modal-form">
          <div className="modal-form-field">
            <label className="model-detail-account-edit-label">
              {t('modelDetail.account.description.label')}
              <textarea
                className="model-detail-account-edit-textarea"
                rows={4}
                value={accountDescriptionDraft}
                onChange={(e) => setAccountDescriptionDraft(e.target.value)}
                placeholder={t('modelDetail.account.description.placeholder')}
              />
            </label>
          </div>
          <div className="modal-form-field">
            <label className="model-detail-account-edit-label">
              {t('modelDetail.account.timezone.label')}
              <TimezoneSelect
                value={timezoneDraft}
                onChange={setTimezoneDraft}
                placeholder={t('modelDetail.account.timezone.placeholder')}
              />
            </label>
          </div>
          {canManageModelDefaultConfig && (
            <div className="modal-form-field">
              <label className="model-detail-account-edit-label">
                {t('modelDetail.account.defaultConfig.label')}
                <select
                  className="model-detail-account-edit-input"
                  value={defaultConfigIdDraft ?? ''}
                  onChange={(e) => setDefaultConfigIdDraft?.(e.target.value)}
                >
                  <option value="">{t('agencySettings.models.defaultConfigActive')}</option>
                  {configs.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name || `Config #${config.id}`}{config.is_active ? ` (${t('agencySettings.models.defaultConfigActiveBadge')})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <small className="model-detail-account-edit-hint">
                {t('modelDetail.account.defaultConfig.hint')}
              </small>
            </div>
          )}
          <div className="modal-form-field">
            <label className="model-detail-account-edit-label">
              {t('modelDetail.account.maxMessagePrice.label')}
              <input
                type="number"
                min={3}
                max={200}
                step={1}
                inputMode="numeric"
                className="model-detail-account-edit-input"
                value={maxMessagePriceDraft ?? ''}
                onChange={(e) => setMaxMessagePriceDraft?.(e.target.value)}
                placeholder={t('modelDetail.account.maxMessagePrice.placeholder')}
              />
            </label>
            <small className="model-detail-account-edit-hint">
              {t('modelDetail.account.maxMessagePrice.hint')}
            </small>
          </div>
        </form>
      </Modal>
    </section>
  )
}
